<template>
  <aside class="support-sidebar">
    <support-search></support-search>

    <div class="support-sidebar__mobile-picker" v-if="!loading && collections.length > 0">
      <select
        class="support-sidebar__select"
        :value="currentCollectionId || ''"
        @change="onCollectionChange"
        aria-label="Browse support categories"
      >
        <option value="" disabled>Browse categories…</option>
        <option
          v-for="collection in collections"
          :key="collection.id"
          :value="collection.slug || collection.id"
        >
          {{ collection.name }}
        </option>
      </select>
    </div>

    <div class="support-sidebar__header">
      <router-link 
        to="/support" 
        class="support-sidebar__link"
        :class="{ 'support-sidebar__link--active': isWelcomeActive }"
      >
        Welcome
      </router-link>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="support-sidebar__loading">
      <p>Loading...</p>
    </div>

    <!-- Collections List -->
    <nav v-if="!loading && collections.length > 0" class="support-sidebar__nav">
      <ul class="support-sidebar__list">
        <li 
          v-for="collection in collections" 
          :key="collection.id"
          class="support-sidebar__item"
        >
          <router-link 
            :to="`/support/${collection.slug || collection.id}`"
            class="support-sidebar__link"
            :class="{ 'support-sidebar__link--active': isActive(collection) }"
          >
            {{ collection.name }}
          </router-link>

          <!-- Show categories if this collection is active -->
          <ul 
            v-if="isActive(collection) && collection.categories && collection.categories.length > 0"
            class="support-sidebar__sublist"
          >
            <li 
              v-for="category in collection.categories" 
              :key="category.id"
              class="support-sidebar__subitem"
            >
              <router-link
                :to="`/support/${collection.slug || collection.id}/category/${category.slug || category.id}`"
                class="support-sidebar__category-link"
                :class="{ 'support-sidebar__category-link--active': isCategoryActive(category) }"
              >
                {{ category.name }}
              </router-link>
            </li>
          </ul>
        </li>
      </ul>
    </nav>
  </aside>
</template>

<script>
import helpScoutService from "@/services/helpscout";
import helpScoutState from "@/services/helpscoutState";
import SupportSearch from "@/components/Support/SupportSearch";

export default {
  name: "SupportSidebarNav",
  components: {
    SupportSearch,
  },
  data() {
    return {
      collections: [],
      loading: true,
    };
  },
  computed: {
    currentCollectionId() {
      return this.$route.params.collectionId;
    },
    currentCategoryId() {
      return this.$route.params.categoryId;
    },
    isWelcomeActive() {
      return this.$route.path === '/support';
    },
  },
  async created() {
    await this.loadCollections();
  },
  methods: {
    onCollectionChange(e) {
      const value = e.target.value;
      if (!value) return;
      this.$router.push(`/support/${value}`);
    },
    async loadCollections() {
      try {
        this.loading = true;
        
        // Use shared state instead of direct API call
        this.collections = await helpScoutState.getCollections(helpScoutService);

        // If we're on a collection page, load its categories
        if (this.currentCollectionId) {
          await this.loadActiveCollectionCategories();
        }
      } catch (error) {
        console.error('Error loading collections for sidebar:', error);
      } finally {
        this.loading = false;
      }
    },
    async loadActiveCollectionCategories() {
      const activeCollection = this.collections.find(
        c => c.id === this.currentCollectionId || c.slug === this.currentCollectionId
      );

      if (activeCollection) {
        try {
          // Use shared state to get categories (with caching)
          const categories = await helpScoutState.getCategories(activeCollection.id, helpScoutService);
          activeCollection.categories = categories;
        } catch (error) {
          console.error('Error loading categories for sidebar:', error);
        }
      }
    },
    isActive(collection) {
      return collection.id === this.currentCollectionId || 
             collection.slug === this.currentCollectionId;
    },
    isCategoryActive(category) {
      return category.id === this.currentCategoryId || 
             category.slug === this.currentCategoryId;
    },
  },
  watch: {
    '$route.params.collectionId': {
      handler() {
        this.loadActiveCollectionCategories();
      },
    },
  },
};
</script>

